import { Alert, Button, Card, Form, Input, Popconfirm, Select, Space, Tag, Typography } from "antd";
import type { ComponentProps, ComponentType } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { dateTimeFormat } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import type { ConsignmentPageResult, ConsignmentQuery, ConsignmentRow } from "./types";

// 旧 Vue useTable 的默认分页大小为 10，首屏和重置查询均沿用该契约。
const defaultQuery: ConsignmentQuery = { page: 1, size: 10 };

// beta.13 的运行时支持 controls 配置，类型声明暂未暴露该兼容字段。
type ProductWeightInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ProductWeightInput = ZhInputNumber as ComponentType<ProductWeightInputProps>;
const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "审核失败",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

type ConsignmentFilterValues = {
    keyword?: string;
    productTypeId?: string;
    supplierId?: string;
    handlerName?: string;
    state?: number;
    publishState?: number;
};

type ConsignmentSpecialist = {
    user: { personId?: number; name?: string; employeeId?: string };
    productCount?: number;
};

export interface ConsignmentPageProps {
    assignConsignment?: (id: number | string, handlerId: number) => Promise<void>;
    closeConsignment?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelf?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (keyword: string) => Promise<ConsignmentSpecialist[]>;
    loadConsignments?: (query: ConsignmentQuery) => Promise<ConsignmentPageResult>;
    updateWeight?: (params: { id: number | string; weight: number }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 寄售商品分页接口。 */
export async function loadConsignments(query: ConsignmentQuery): Promise<ConsignmentPageResult> {
    const response = await request.service.get<ConsignmentPageResult>("/admin/product-consignment/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 寄售商品权重更新接口。 */
export async function updateConsignmentWeight(params: { id: number | string; weight: number }): Promise<void> {
    await request.service.put(`/admin/product-consignment/weight/${params.id}`, params);
}

/** 复用 Vue 商品下架接口。 */
export async function downShelfConsignment(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-consignment/down-shelf/${id}`);
}

/** 复用 Vue 商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeConsignment(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-consignment/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 专员候选查询接口。 */
export async function findServiceSpecials(keyword: string): Promise<ConsignmentSpecialist[]> {
    const response = await request.service.get<ConsignmentSpecialist[]>(
        `/admin/product-consignment/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 寄售商品指派接口。 */
export async function assignConsignment(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-consignment/assign/${id}/${handlerId}`);
}

/** 将 Vue 寄售商品列表返回的状态枚举统一为 React 操作和文案使用的数字。 */
function normalizeConsignmentRows(rows?: ConsignmentRow[]): ConsignmentRow[] {
    return (rows || []).map(row => ({
        ...row,
        productConsignment: {
            ...row.productConsignment,
            state: row.productConsignment.state == null ? undefined : Number(row.productConsignment.state),
            publishState:
                row.productConsignment.publishState == null ? undefined : Number(row.productConsignment.publishState)
        }
    }));
}

/** 寄售商品列表，保留 Vue 首屏字段、权限和状态驱动操作。 */
export function ConsignmentPage({
    assignConsignment: assign = assignConsignment,
    closeConsignment: close = closeConsignment,
    downShelf: removeFromShelf = downShelfConsignment,
    findServiceSpecials: findSpecials = findServiceSpecials,
    loadConsignments: load = loadConsignments,
    updateWeight: saveWeight = updateConsignmentWeight,
    permissions
}: ConsignmentPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [items, setItems] = useState<ConsignmentRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<ConsignmentRow | null>(null);
    const [specialists, setSpecialists] = useState<ConsignmentSpecialist[]>([]);
    const [specialistKeyword, setSpecialistKeyword] = useState("");
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [query, setQuery] = useState<ConsignmentQuery>(defaultQuery);
    const [editingWeightId, setEditingWeightId] = useState<number | string | null>(null);
    const [filterForm] = Form.useForm<ConsignmentFilterValues>();

    const refresh = async (targetQuery: ConsignmentQuery = query) => {
        const result = await load(targetQuery);
        setItems(normalizeConsignmentRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeConsignmentRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("寄售商品加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    const handleDownShelf = async (id?: number | string) => {
        if (id == null) return;
        try {
            await removeFromShelf(id);
            await refresh();
        } catch {
            // 下架接口或刷新失败时保留当前商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("商品下架失败");
        }
    };

    const handleClose = async (row: ConsignmentRow) => {
        const id = row.productConsignment.id;
        if (id == null) return;
        // Vue 关闭接口要求清理主图、视频和附加图片的文件 ID。
        const unActiveFileIds = [
            row.mainFile?.id,
            row.videoFile?.id,
            ...(row.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            await refresh();
        } catch {
            // 关闭接口或刷新失败时保留当前商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("商品关闭失败");
        }
    };

    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            setSpecialists(await findSpecials(keyword));
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    const openAssign = (row: ConsignmentRow) => {
        setAssignTarget(row);
        setSpecialistKeyword("");
        setSelectedHandlerId(row.productConsignment.handlerId);
        void searchSpecialists("");
    };

    const handleAssign = async () => {
        if (!assignTarget || selectedHandlerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        const id = assignTarget.productConsignment.id;
        if (id == null) return;
        setAssignLoading(true);
        try {
            await assign(id, selectedHandlerId);
            setAssignTarget(null);
            await refresh();
        } catch {
            setAssignError("变更指派失败");
        } finally {
            setAssignLoading(false);
        }
    };

    // 将旧 Vue 商品编号/标题筛选转换为寄售分页接口的 `keyword` 参数。
    const submitFilters = (values: ConsignmentFilterValues) => {
        setErrorMessage(null);
        setQuery({
            ...defaultQuery,
            ...Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined && value !== ""))
        });
    };

    // 清空关键词并恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 寄售商品的嵌套字段、状态枚举和权限动作。
    const columns: TableColumnProps<ConsignmentRow>[] = [
        {
            key: "id",
            label: "商品编号",
            prop: "productConsignment",
            render: ({ row }) => (
                <Button
                    aria-label="查看商品详情"
                    type="link"
                    onClick={() =>
                        void navigate(`/productManagement/consignment/detail?id=${row.productConsignment.id || ""}`)
                    }
                >
                    {row.productConsignment.id || "-"}
                </Button>
            )
        },
        {
            key: "weight",
            label: "权重值",
            // 对齐 Vue `productConsignment.weight` 的 custom 排序字段。
            prop: "productConsignment.weight",
            sortable: true,
            render: ({ row }) => {
                const id = row.productConsignment.id;
                if (id != null && editingWeightId === id) {
                    return (
                        <ProductWeightInput
                            aria-label="寄售商品权重值"
                            autoFocus
                            controls={false}
                            max={10000}
                            min={0}
                            precision={0}
                            defaultValue={row.productConsignment.weight ?? ""}
                            onBlur={event => {
                                const input = event.currentTarget as HTMLInputElement;
                                const weight = Number(input.value);
                                setEditingWeightId(null);
                                if (input.value === "" || !Number.isFinite(weight)) return;
                                void saveWeight({ id, weight });
                            }}
                        />
                    );
                }
                return (
                    <Space size={4}>
                        <span>{row.productConsignment.weight ?? 0}</span>
                        {id != null ? (
                            <Button
                                aria-label="编辑寄售商品权重值"
                                icon={<span aria-hidden="true">✎</span>}
                                size="small"
                                type="link"
                                onClick={() => setEditingWeightId(id)}
                            />
                        ) : null}
                    </Space>
                );
            }
        },
        {
            key: "title",
            label: "主图/标题",
            prop: "productConsignment",
            render: ({ row }) => (
                <Space>
                    {row.mainFile?.path ? (
                        <img alt="商品主图" className="h-8 w-8 object-contain" src={row.mainFile.path} />
                    ) : (
                        <span className="product-image-placeholder">暂无图片</span>
                    )}
                    <span>{row.productConsignment.title || "-"}</span>
                </Space>
            )
        },
        {
            key: "productType",
            label: "品类",
            prop: "productType",
            render: ({ row }) =>
                row.productType?.name
                    ? `${row.productType.name}${row.productType.areaAbbreviation ? ` (${row.productType.areaAbbreviation})` : ""}`
                    : "-"
        },
        {
            key: "supplier",
            label: "供应商",
            prop: "productConsignment",
            render: ({ row }) => getEnterpriseName(row.productConsignment.supplierEnterprise as never)
        },
        {
            key: "unitPrice",
            label: "销售单价 (元/吨)",
            prop: "productConsignment",
            render: ({ row }) => row.productConsignment.unitPrice ?? "-"
        },
        {
            key: "minOrderTon",
            label: "起订吨数 (吨)",
            prop: "productConsignment",
            render: ({ row }) => row.productConsignment.minOrderTon ?? "-"
        },
        {
            key: "inventory",
            label: "库存量（吨）",
            prop: "productConsignment",
            render: ({ row }) => row.productConsignment.inventoryQuantity ?? 0
        },
        {
            key: "updatedTime",
            label: "更新时间",
            prop: "productConsignment",
            render: ({ row }) => dateTimeFormat(row.productConsignment.updatedTime)
        },
        {
            key: "handlerName",
            label: "商品专员",
            prop: "productConsignment",
            render: ({ row }) => row.productConsignment.handlerName || "-"
        },
        {
            key: "state",
            label: "商品状态",
            prop: "productConsignment",
            render: ({ row }) => <Tag>{productStateLabels[row.productConsignment.state || 0] || "-"}</Tag>
        },
        {
            key: "publishState",
            label: "发布状态",
            prop: "productConsignment",
            render: ({ row }) => <Tag>{publishStateLabels[row.productConsignment.publishState || 0] || "-"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            prop: "productConsignment",
            fixed: "right",
            width: 160,
            render: ({ row }) => {
                const id = row.productConsignment.id;
                const isPublished = row.productConsignment.state === 1 && row.productConsignment.publishState === 3;
                const canAudit = canManage && row.productConsignment.publishState === 1;
                const canAssign =
                    canManage &&
                    !(
                        row.productConsignment.state === 2 &&
                        [4, 5, 6].includes(row.productConsignment.publishState || 0)
                    );
                const canEdit =
                    canDeal &&
                    row.productConsignment.state === 2 &&
                    [2, 4, 6].includes(row.productConsignment.publishState || 0);
                const canClose =
                    canClosePermission &&
                    row.productConsignment.state === 2 &&
                    [1, 2, 4, 6].includes(row.productConsignment.publishState || 0);
                // Vue 列表按状态和权限组合展示可执行操作。
                if (!canAssign && !canAudit && !canEdit && !canClose && !(canDeal && isPublished)) return null;
                return (
                    <Space>
                        {canAssign ? (
                            <Button
                                aria-label="变更指派"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => openAssign(row)}
                            >
                                变更指派
                            </Button>
                        ) : null}
                        {canAudit ? (
                            <Button
                                aria-label="审核商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void navigate(`/productManagement/consignment/audit?id=${id || ""}`)}
                            >
                                审核
                            </Button>
                        ) : null}
                        {canEdit ? (
                            <Button
                                aria-label="编辑商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void navigate(`/productManagement/consignment/edit?id=${id || ""}`)}
                            >
                                编辑
                            </Button>
                        ) : null}
                        {canClose ? (
                            <Popconfirm
                                cancelText="取消"
                                okText="确定"
                                title="确认关闭该商品吗？"
                                onConfirm={() => void handleClose(row)}
                            >
                                <Button aria-label="关闭商品" disabled={banButton} size="small" type="link">
                                    关闭
                                </Button>
                            </Popconfirm>
                        ) : null}
                        {canDeal && isPublished ? (
                            <Popconfirm
                                cancelText="取消"
                                okText="确定"
                                title="确认下架该商品吗？"
                                onConfirm={() => void handleDownShelf(id)}
                            >
                                <Button aria-label="下架商品" disabled={banButton} size="small" type="link">
                                    下架
                                </Button>
                            </Popconfirm>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="consignment-page" className="flex h-full min-h-full flex-col">
            <ZhDialog
                open={Boolean(assignTarget)}
                title="变更指派商品专员"
                width={842}
                onCancel={() => setAssignTarget(null)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignTarget(null)}>取消</Button>
                        <Button
                            aria-label="确认指派"
                            loading={assignLoading}
                            type="primary"
                            onClick={() => void handleAssign()}
                        >
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <Space.Compact block>
                    <Input
                        aria-label="专员姓名"
                        placeholder="请输入姓名"
                        value={specialistKeyword}
                        onChange={event => setSpecialistKeyword(event.target.value)}
                    />
                    <Button type="primary" onClick={() => void searchSpecialists(specialistKeyword)}>
                        查询专员
                    </Button>
                </Space.Compact>
                {assignError ? <p role="alert">{assignError}</p> : null}
                <div aria-label="专员列表" className="max-h-72 overflow-auto" role="radiogroup">
                    {specialists.map(specialist => {
                        const personId = specialist.user.personId;
                        if (personId == null) return null;
                        return (
                            <label key={personId} className="mt-3 block">
                                <input
                                    aria-label={specialist.user.name || String(personId)}
                                    checked={selectedHandlerId === personId}
                                    name="consignment-handler"
                                    type="radio"
                                    onChange={() => setSelectedHandlerId(personId)}
                                />
                                <span className="ml-2">
                                    <span>{specialist.user.name || "未命名专员"}</span> (
                                    {specialist.user.employeeId || "-"})
                                </span>
                            </label>
                        );
                    })}
                </div>
            </ZhDialog>
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ConsignmentRow>
                    data={items}
                    columns={columns}
                    emptyText="暂无寄售商品"
                    pagination={false}
                    rowKey={row => String(row.productConsignment.id)}
                    onSortChange={({ prop, order }) => {
                        if (prop !== "productConsignment.weight") return;
                        // 沿用 Vue sort-change：排序后回到第一页，并清理取消排序时的旧参数。
                        setQuery(currentQuery => {
                            const nextQuery: ConsignmentQuery = { ...currentQuery, page: 1 };
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                            if (order) {
                                nextQuery.sortKey = "weight";
                                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                            }
                            return nextQuery;
                        });
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                寄售商品管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canDeal ? (
                                    <Button
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/productManagement/consignment/add")}
                                    >
                                        新增商品
                                    </Button>
                                ) : null}
                                <Form.Item name="keyword">
                                    <Input placeholder="请输入商品编号或商品标题" />
                                </Form.Item>
                                <Form.Item name="productTypeId">
                                    <Input placeholder="请选择品类" />
                                </Form.Item>
                                <Form.Item name="supplierId">
                                    <Input placeholder="请选择供应商" />
                                </Form.Item>
                                <Form.Item name="state">
                                    <Select
                                        allowClear
                                        placeholder="请选择商品状态"
                                        style={{ width: 140 }}
                                        options={Object.entries(productStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item name="publishState">
                                    <Select
                                        allowClear
                                        placeholder="请选择发布状态"
                                        style={{ width: 140 }}
                                        options={Object.entries(publishStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item name="handlerName">
                                    <Input placeholder="请输入商品专员搜索" />
                                </Form.Item>
                                <Button
                                    aria-label="搜索"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showTotal={value => `共${value}条数据`}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 寄售商品分页契约，切换页大小后回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ConsignmentPage;
